import { useState } from "react";
import { Link } from "react-router-dom";
import { Printer, Users, Truck, Clock, Euro, ChevronDown } from "lucide-react";
import { useApi } from "../lib/api";
import { PageHeader, Loading, Empty, Kpi, DataState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WhatsAppButton } from "../components/Integrations";
import { WorkerDayList, OffDayBanner } from "../components/WorkerDay";
import { eur, fmtDate, fmtLong, num, todayISO, addDays } from "../lib/format";

function SiteCard({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="surface overflow-hidden fade-up" data-testid={`ops-site-${r.site.id}`}>
      <div className="p-5">
        <div className="flex justify-between gap-3">
          <div><div className="font-mono text-xs text-amber-400">{r.site.code}</div><Link to={`/cantieri/${r.site.id}`} className="text-xl font-bold font-heading uppercase hover:text-amber-300">Cantiere {r.site.name}</Link>
            <div className="text-sm text-muted-foreground">Responsabile: <span className="text-foreground">{r.site.manager_name || "—"}</span></div></div>
          <div className="text-right"><div className="eyebrow">Totale</div><div className="num text-2xl font-bold text-amber-300" data-testid={`ops-total-${r.site.id}`}>{eur(r.total_cost)}</div></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 text-sm">
          {[["Operai", r.worker_count], ["Ore", num(r.hours)], ["Costo manodopera", eur(r.labor_cost)], ["Mezzi", r.vehicle_count], ["Costo mezzi", eur(r.vehicle_cost)]].map(([l, v]) => (
            <div key={l} className="rounded-lg bg-background/60 border border-border p-2.5"><div className="eyebrow">{l}</div><div className="num font-semibold mt-0.5">{v}</div></div>
          ))}
        </div>
      </div>
      <button onClick={() => setOpen(!open)} className="w-full border-t border-border px-5 py-2.5 text-sm text-muted-foreground hover:text-white flex items-center justify-between" data-testid={`ops-toggle-${r.site.id}`}>
        Dettaglio operai e mezzi <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 grid md:grid-cols-2 gap-4 text-sm">
          <div>{r.workers.map((w) => <div key={w.assignment_id} className="flex justify-between py-1.5 border-b border-border/50"><span>{w.name} <span className="text-muted-foreground text-xs">{w.qualification}</span></span><span className="num">{w.hours}h · {eur(w.cost)}</span></div>)}</div>
          <div>{r.vehicles.map((v) => <div key={v.assignment_id} className="flex justify-between py-1.5 border-b border-border/50"><span>{v.label}</span><span className="num">{eur(v.cost)}</span></div>)}</div>
        </div>
      )}
    </div>
  );
}

function CostSummary() {
  const [period, setPeriod] = useState("week");
  const { data } = useApi("/costs", { period });
  return (
    <section className="surface p-4 sm:p-5 mt-6" data-testid="cost-summary">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div><div className="eyebrow">Calcolo costi</div><h2 className="text-lg font-semibold">Riepilogo costi per cantiere</h2>{data && <div className="text-xs text-muted-foreground num">{fmtDate(data.start)} → {fmtDate(data.end)}</div>}</div>
        <Tabs value={period} onValueChange={setPeriod}><TabsList>
          <TabsTrigger value="day" data-testid="costs-day">Giornaliero</TabsTrigger><TabsTrigger value="week" data-testid="costs-week">Settimanale</TabsTrigger><TabsTrigger value="month" data-testid="costs-month">Mensile</TabsTrigger>
        </TabsList></Tabs>
      </div>
      <DataState loading={!data} empty={!data?.sites?.length} emptyTitle="Nessun costo nel periodo"><CostTable data={data} /></DataState>
    </section>
  );
}

function CostTable({ data }) {
  return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border"><th className="py-2">Cantiere</th><th className="text-right">Ore</th><th className="text-right">Manodopera</th><th className="text-right">Mezzi</th><th className="text-right">Totale</th></tr></thead>
            <tbody>
              {data.sites.map((r) => <tr key={r.site_id} className="border-b border-border/50"><td className="py-2.5">{r.site_name}</td><td className="text-right num">{num(r.hours)}</td><td className="text-right num">{eur(r.labor_cost)}</td><td className="text-right num">{eur(r.vehicle_cost)}</td><td className="text-right num font-semibold text-amber-300">{eur(r.total_cost)}</td></tr>)}
              <tr className="font-bold"><td className="py-2.5">TOTALE</td><td className="text-right num">{num(data.totals.hours)}</td><td className="text-right num">{eur(data.totals.labor_cost)}</td><td className="text-right num">{eur(data.totals.vehicle_cost)}</td><td className="text-right num text-amber-300" data-testid="costs-total">{eur(data.totals.total_cost)}</td></tr>
            </tbody>
          </table>
        </div>
  );
}

function DayBody({ data }) {
  const [view, setView] = useState("sites");
  return (
    <>
      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList>
          <TabsTrigger value="sites" data-testid="ops-view-sites">Per cantiere</TabsTrigger>
          <TabsTrigger value="workers" data-testid="ops-view-workers">Per operaio ({data.workers.length})</TabsTrigger>
        </TabsList>
      </Tabs>
      {view === "workers" && <WorkerDayList rows={data.workers} showCost />}
      {view === "sites" && (!data.sites.length ? <div className="surface"><Empty title="Nessun cantiere operativo in questa data" /></div> : (
        <div className="grid xl:grid-cols-2 gap-4">{data.sites.map((r) => <SiteCard key={r.site.id} r={r} />)}</div>
      ))}
    </>
  );
}

export default function Operations() {
  const [day, setDay] = useState(todayISO());
  const { data, loading } = useApi("/operations", { day });
  return (
    <div>
      <PageHeader eyebrow="Controllo giornaliero" title="Dashboard operativa" subtitle={<span className="capitalize">{fmtLong(day)}</span>}
        actions={<>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-auto h-10" data-testid="ops-date-input" />
          <Button variant="outline" onClick={() => window.open(`/stampa-presenze?date=${day}`, "_blank")} data-testid="print-attendance-btn"><Printer className="h-4 w-4 mr-2" />STAMPA FOGLIO PRESENZE</Button>
          <WhatsAppButton defaultDay={addDays(day, 1)} />
        </>} />
      {loading && !data ? <Loading /> : (
        <>
          <OffDayBanner reason={data.off_reason} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Kpi label="Operai in cantiere" value={data.totals.workers} icon={Users} accent="bg-amber-500" testid="ops-kpi-workers" />
            <Kpi label="Mezzi impiegati" value={data.totals.vehicles} icon={Truck} accent="bg-sky-500" testid="ops-kpi-vehicles" />
            <Kpi label="Ore totali" value={num(data.totals.hours)} icon={Clock} accent="bg-emerald-500" testid="ops-kpi-hours" />
            <Kpi label="Costo totale giorno" value={eur(data.totals.total_cost)} icon={Euro} accent="bg-red-500" testid="ops-kpi-cost" />
          </div>
          <DayBody data={data} />
        </>
      )}
      <CostSummary />
    </div>
  );
}
